// The screen a route shows when its loader throws, and the one a URL that
// matches nothing shows.
//
// WHY IT EXISTS. TanStack Router's DEFAULT error component renders
// "Something went wrong! / Show Error" with one control on the page — "Hide
// Error" — and, expanded, the thrown string. Measured on dev: a single
// transient `429` on `/auth/me` (Cloud Run's own, body "Rate exceeded.") blanked
// the whole app into that screen. `/auth/me` answered 200 on the very next poll
// — nothing was actually wrong — and the only way out was a manual browser
// reload. `/projects/999999` rendered
// `404 : {"statusCode":404,"code":"gitlab_api_error",…"GitLab GET
// /api/v4/projects/999999/issues returned HTTP 404"}`: the internal REST route,
// in the browser, as the user-facing copy.
//
// Two things fix both: a way back (retry, which re-runs the loader, and a link
// home), and copy that is a sentence. The raw string still ships, demoted under
// a disclosure — the transparency convention the refine surface follows, where
// the operator's string is a note under the human one, never instead of it.

import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useState, type JSX } from "react";

import { useT, type MessageKey } from "../i18n/index.js";

import { HttpError } from "./http";

/** Which sentence explains this failure. Status-driven, because the four cases
 *  want four different next moves — wait and retry, check the link, sign in
 *  again, or none of the above. */
const bodyKeyFor = (status: number | null): MessageKey => {
  if (status === 404) return "routeError.body.notFound";
  if (status === 429 || status === 503) return "routeError.body.busy";
  if (status === 401 || status === 403) return "routeError.body.denied";
  return "routeError.body.generic";
};

export const RouteError = ({
  error,
  reset,
}: ErrorComponentProps): JSX.Element => {
  const t = useT();
  const router = useRouter();
  const [shown, setShown] = useState(false);
  const status = error instanceof HttpError ? error.status : null;
  const detail =
    error instanceof Error ? error.message : String(error as unknown);
  return (
    <div className="route-error">
      <h1 className="route-error-title">{t("routeError.headline")}</h1>
      <p className="route-error-body">{t(bodyKeyFor(status))}</p>
      <div className="route-error-actions">
        <button
          type="button"
          className="route-error-btn"
          onClick={() => {
            // `reset` clears the boundary; `invalidate` re-runs the loader that
            // threw. Without the second, the retry re-renders the same error.
            reset();
            void router.invalidate();
          }}
        >
          {t("common.retry")}
        </button>
        <a className="route-error-btn is-ghost" href="/">
          {t("routeError.goHome")}
        </a>
      </div>
      <button
        type="button"
        className="route-error-toggle"
        onClick={() => setShown((v) => !v)}
      >
        {shown ? t("routeError.hideDetail") : t("routeError.showDetail")}
      </button>
      {shown && <pre className="route-error-detail">{detail}</pre>}
    </div>
  );
};

/** A URL that matches no route. The router's own default is a bare
 *  "Not Found" with no navigation at all. */
export const RouteNotFound = (): JSX.Element => {
  const t = useT();
  return (
    <div className="route-error">
      <h1 className="route-error-title">{t("routeError.notFound.headline")}</h1>
      <p className="route-error-body">{t("routeError.body.notFound")}</p>
      <div className="route-error-actions">
        <a className="route-error-btn" href="/">
          {t("routeError.goHome")}
        </a>
      </div>
    </div>
  );
};
